/**
 * ProductComparisonCard — the ONE source-versus-Walmart card (Stage Z PR B).
 *
 * Built on the existing Auto Sourcing card (`DealFlowCard`) rather than replacing
 * it: the visual language, media isolation and profitability gating all stay
 * exactly as shipped. This wrapper adds what was missing for reuse:
 *
 *   * real retailer identity on the BUY side (logo + name, not "Source");
 *   * an explicit Walmart identity on the SELL side;
 *   * a single `variant` switch so Saved Leads can render comfortable/compact
 *     without a second card implementation.
 *
 * Data separation is inherited from `DealFlowCard`/`ProductCardMedia`: the BUY
 * panel only ever uses source fields, the SELL panel only Walmart fields, and a
 * failed Walmart image becomes a placeholder rather than borrowing the source
 * photo (Stage Y P0-A).
 */
import React, { useMemo, useState } from "react";
import { DealFlowCard } from "@/components/deal-flow/DealFlowCard";
import RetailerLogo from "@/components/auto-sourcing/RetailerLogo";
import { WalmartLogoMark } from "@/components/common/WalmartLogoMark";
import { panelLabels, retailerName, retailerRecord } from "@/lib/leads/retailerIdentity";
import { cn } from "@/lib/utils";
import {
  CollectionPicker,
  useLeadCollections,
} from "@/components/leads/CollectionPicker";

/** Compact `source` object shaped for `RetailerLogo`. */
export function retailerSourceFor(sourceId, directory = null) {
  const record = retailerRecord(sourceId, directory);
  if (record) return record;
  const name = retailerName(sourceId, directory);
  return name ? { source_id: sourceId, name } : null;
}

/**
 * Retailer identity strip shown above the BUY/SELL pair.
 *
 * Kept separate from the media panel so the panel keeps its exact Stage Y
 * behaviour while the label is unambiguous.
 */
export function RetailerIdentityStrip({ lead, sources = [], directory = null }) {
  const labels = panelLabels({ sourceId: lead?.sourceId, directory });
  const source = retailerSourceFor(lead?.sourceId, directory || sources);
  const hasWalmart = Boolean(lead?.walmartItemId);

  return (
    <div
      className="pc-identity-strip flex items-center justify-between gap-2 px-2.5 pt-1"
      data-testid={`product-comparison-identity-${lead?.id}`}
    >
      <span
        className="pc-identity-side flex items-center gap-1.5 min-w-0"
        data-testid={`product-comparison-buy-label-${lead?.id}`}
        data-retailer-id={lead?.sourceId || undefined}
        title={`Buy from ${labels.buyLabel}`}
      >
        {source ? (
          <RetailerLogo source={source} size="sm" variant="tile" />
        ) : (
          <span className="pc-identity-initial" aria-hidden="true">?</span>
        )}
        <span className="pc-identity-name truncate">{labels.buyLabel}</span>
      </span>
      <span
        className="pc-identity-side flex items-center gap-1.5 shrink-0"
        data-testid={`product-comparison-sell-label-${lead?.id}`}
        title={hasWalmart ? "Sell on Walmart" : "No matched Walmart listing"}
      >
        <WalmartLogoMark size={12} />
        <span className="pc-identity-name">{labels.sellLabel}</span>
      </span>
    </div>
  );
}

/**
 * @param {"comfortable"|"compact"} variant
 *   comfortable: today's Auto Sourcing card, unchanged.
 *   compact: same data and actions, tighter media and metrics.
 */
export function ProductComparisonCard({
  lead,
  sources = [],
  directory = null,
  variant = "comfortable",
  onSave,
  onAnalyze,
  onOpen,
  onAddToCollection,
  onAlerts,
  className,
}) {
  const compact = variant === "compact";

  const decorated = useMemo(
    () => (lead ? { ...lead, compact } : lead),
    [lead, compact],
  );

  const [pickerOpen, setPickerOpen] = useState(false);
  const collectionState = useLeadCollections(lead);

  if (!lead) return null;

  return (
    <div
      className={cn("product-comparison-card", compact && "is-compact", className)}
      data-testid={`product-comparison-card-${lead.id}`}
      data-variant={variant}
    >
      <RetailerIdentityStrip lead={lead} sources={sources} directory={directory} />
      <DealFlowCard
        lead={decorated}
        onSave={onSave}
        onAnalyze={onAnalyze}
        onOpen={onOpen}
        onAddToCollection={onAddToCollection || (() => setPickerOpen(true))}
        onAlerts={onAlerts}
      />
      {pickerOpen ? (
        <div
          className="as-sl-modal-backdrop"
          data-testid={`collection-modal-${lead.id}`}
          onClick={(e) => {
            if (e.target === e.currentTarget) setPickerOpen(false);
          }}
        >
          <div className="as-sl-modal" role="dialog" aria-modal="true">
            <h3>Add to collection</h3>
            <CollectionPicker
              lead={lead}
              collections={collectionState.collections}
              memberIds={collectionState.memberIds}
              busyIds={collectionState.busyIds}
              creating={collectionState.creating}
              onToggle={collectionState.toggle}
              onCreate={collectionState.createAndAdd}
              onDone={() => setPickerOpen(false)}
              onRefresh={collectionState.refresh}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default ProductComparisonCard;
